import { useEffect, useState } from 'react';

export function useOneTrustBannerHandler() {
  const [isOneTrustBannerVisible, setIsOneTrustBannerVisible] = useState(false);

  const handleBannerClickPrevention = (banner: HTMLElement) => {
    const preventPropagation = (event: Event) => {
      event.stopPropagation();
    };

    banner.addEventListener('click', preventPropagation, false);

    return () => {
      banner.removeEventListener('click', preventPropagation, false);
    };
  };

  const handleBannerFind = (
    node: HTMLElement,
    cleanupFunctions: Array<() => void>,
  ): boolean => {
    setIsOneTrustBannerVisible(node.checkVisibility());
    const cleanup = handleBannerClickPrevention(node);
    cleanupFunctions.push(cleanup);
    return true; // mark found
  };

  // Helper: attach banner handler if present under a given root
  const attachBannerIfPresent = (
    root: ParentNode | Document,
    cleanupFunctions: Array<() => void>,
  ): boolean => {
    const banner = root.querySelector<HTMLElement>('#onetrust-banner-sdk');
    if (banner) {
      return handleBannerFind(banner, cleanupFunctions);
    }
    return false;
  };

  useEffect(() => {
    const cleanupFunctions: Array<() => void> = [];

    // Inject CSS rule once instead of mutating styles with JS
    const style = document.createElement('style');
    style.textContent = `
      body #onetrust-consent-sdk,
      body #onetrust-consent-sdk *,
      body #onetrust-banner-sdk,
      body .onetrust-pc-dark-filter,
      body .ot-fade-in,
      body [class*="onetrust"],
      body [id*="onetrust"] {
        pointer-events: auto;
      }
    `;
    document.head.appendChild(style);

    // ---- foundBanner flag (new) ----
    let foundBanner = false;

    // ---- Observer that watches only the OneTrust root once available ----
    let containerObserver: MutationObserver | null = null;
    const startContainerObserver = (container: HTMLElement) => {
      // Initial pass under the container
      foundBanner = attachBannerIfPresent(container, cleanupFunctions);

      // Observer that watches only the OneTrust container once available
      // Note that we disconnected the bodyObserver after this container was found
      containerObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (!(node instanceof HTMLElement)) continue;

            if (node.matches('#onetrust-banner-sdk')) {
              foundBanner = handleBannerFind(node, cleanupFunctions);
            }

            node
              .querySelectorAll<HTMLElement>('#onetrust-banner-sdk')
              .forEach((banner) => {
                foundBanner = handleBannerFind(banner, cleanupFunctions);
              });
          }
        }

        // Early disconnect once we've bound the banner
        if (foundBanner) {
          containerObserver?.disconnect();
        }
      });

      containerObserver.observe(container, { childList: true, subtree: true });

      // If initial pass already found it, disconnect immediately
      if (foundBanner) {
        containerObserver.disconnect();
      }
    };

    // ---- Bootstrapping: if container exists now, use it; otherwise watch body until it appears ----
    const containerNow = document.querySelector<HTMLElement>(
      '#onetrust-consent-sdk',
    );

    // Body observer (only used until we find the OneTrust container)
    let bodyObserver: MutationObserver | null = null;

    if (containerNow) {
      // Container already present — observe it directly
      startContainerObserver(containerNow);
    } else {
      // Observe body until #onetrust-consent-sdk appears, then switch
      bodyObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (!(node instanceof HTMLElement)) continue;

            if (node.matches('#onetrust-consent-sdk')) {
              startContainerObserver(node);
              bodyObserver?.disconnect();
              bodyObserver = null;
              return;
            }

            const maybeContainer = node.querySelector<HTMLElement>(
              '#onetrust-consent-sdk',
            );
            if (maybeContainer) {
              startContainerObserver(maybeContainer);
              bodyObserver?.disconnect();
              bodyObserver = null;
              return;
            }
          }
        }
      });

      bodyObserver.observe(document.body, { childList: true, subtree: true });
    }

    // Also check for an already-present banner at the document level (covers rare timing cases)
    foundBanner = attachBannerIfPresent(document, cleanupFunctions);

    // Cleanup
    return () => {
      bodyObserver?.disconnect();
      containerObserver?.disconnect();
      cleanupFunctions.forEach((cleanup) => cleanup());
      style.remove();
    };
  }, []);

  return { isOneTrustBannerVisible: isOneTrustBannerVisible };
}
